/**
 * Seed Data — deterministic development dataset aligned with documentation.
 *
 * Recreates the rich synthetic portfolio described in docs/narratives/architecture/seed-data.md
 * using the streamlined flat report architecture. The generator provisions
 * companies, sites, report history, non-conformities, and actions so that
 * the inspector UI can exercise offline-first flows with realistic content.
 */

import { CompaniesRepo } from '../../repos/companiesRepo';
import { SitesRepo } from '../../repos/sitesRepo';
import { ActionsRepo } from '../../repos/actionsRepo';
import { syncGuPublicationAction } from '../../actions/guPublicationActions';
import { ActionSyncService } from '../../services/actionSyncService';
import { ReportsRepo } from '../../repos/reportsRepo';
import { TemplatesRepo } from '../../repos/templatesRepo';
import {
  db,
  type ContactRow,
  type UnitRow,
  type ActivityRow,
  type ActivityCategory,
} from '../../db';
import { createDefaultTemplate } from '../../defaultTemplate';
import { createReportFromTemplate, createReportWithTracking } from '../createReportFromTemplate';
import type { TemplateContextOverrides } from '../templateContext';
import {
  VisitContext,
  type VisitContextType,
  type Company,
  type Site,
  type ReportTemplate,
  type Report,
  type VisitUnitCheck,
  type VisitDpiInventory,
} from '../../schemas';
import { buildKeypath, generateHumanReadableId } from '../../idGenerator';
import {
  SCORECARD_DATE_FIELD_ID,
  SCORECARD_FIELD_TO_CONTEXT_KEY,
  SCORECARD_SECTION_ID,
} from '../../scorecardConfig';
import { newId } from '../../../utils/ids';
import {
  captureDatabaseBundle,
  replaceDatabaseFromBundle,
  type DataBundle,
} from '../../export/dataTransfer';
import { writeCurrentDevDataVersion } from '../../devDatabaseVersion';
import { filterPersonnelForSite } from '../../personnel/personnelScope';
import { createSyncMetadata } from '../../sync/syncMetadata';
import { createRng, pick, randomInt, shuffle, type Rng } from './rng';
import {
  addDays,
  addMonths,
  createWeekdayPlanner,
  formatControlPeriodRange,
  scheduleActionDueDate,
  toIsoDate,
  toIsoDateTime,
} from './dates';
import { sectionId } from './ids';
import { INSPECTORS } from './people';
import { ACTION_TITLE_POOL, OPEN_ACTION_TARGETS } from './constants';
import { clonePersonnel, evolvePersonnel, generateCompany } from './seedCompanies';
import type {
  EmergencyDrillSeed,
  FollowUp,
  GeneratedCompany,
  GeneratedReport,
  GeneratedSite,
  InfrastructureCheckSeed,
  RegistersSeed,
  ScenarioOptions,
  ScenarioResult,
  ScorecardSeedRow,
  SimulationSeed,
  SiteIssueRecord,
  TrainingUpdateSeed,
  VisitSeedData,
} from './types';

// ---------------------------------------------------------------------------
// Configuration constants
// ---------------------------------------------------------------------------

const TOTAL_COMPANIES = 10;
const REPORT_HISTORY_BASE = 2; // Company index adds depth: history = base + index
const DEFAULT_SEED = 0xc0ffee;
// Demo-mode-specific seeding flag. Only used in demo mode to track if sample data
// has been seeded. Real mode never seeds data.
const SEED_STORAGE_KEY = 'demo-mode-seeded';
const LEGACY_SEED_STORAGE_KEYS = [
  'industrial-audit-dev-data-seeded', // Previous key before demo/real mode split
  'audit-system-v5-english-schema-seeded',
  'audit-system-v4-seeded',
] as const;

const WORKDAY_TIME_SLOTS = [
  { hour: 8, minute: 30 },
  { hour: 9, minute: 0 },
  { hour: 9, minute: 30 },
  { hour: 10, minute: 0 },
  { hour: 10, minute: 30 },
  { hour: 11, minute: 0 },
  { hour: 11, minute: 30 },
  { hour: 12, minute: 0 },
  { hour: 14, minute: 0 },
  { hour: 14, minute: 30 },
  { hour: 15, minute: 0 },
  { hour: 15, minute: 30 },
  { hour: 16, minute: 0 },
  { hour: 16, minute: 30 },
  { hour: 17, minute: 0 },
];

const applyWorkingHours = (date: Date, rng: Rng): Date => {
  const slotIndex = randomInt(rng, 0, WORKDAY_TIME_SLOTS.length - 1);
  const slot = WORKDAY_TIME_SLOTS[slotIndex]!;
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    slot.hour,
    slot.minute,
    0,
    0
  );
};

const INFRASTRUCTURE_FIELDS = [
  { id: 'lighting-exit', label: 'Illuminazione esodo' },
  { id: 'lighting-intervention', label: 'Illuminazione intervento' },
  { id: 'door-closure', label: 'Chiusura porte' },
  { id: 'cctv', label: 'Videosorveglianza' },
  { id: 'intrusion', label: 'Allarme antintrusione' },
  { id: 'vvf-docs', label: 'Cassetta documenti VVF' },
] as const;

type InfrastructureFieldId = (typeof INFRASTRUCTURE_FIELDS)[number]['id'];

const REGISTER_DEFINITIONS = [
  {
    key: 'maintenance',
    type: 'manutenzione',
    title: 'Aggiornare registro manutenzione',
  },
  {
    key: 'safety',
    type: 'sicurezza',
    title: 'Aggiornare registro sicurezza',
  },
  {
    key: 'charge',
    type: 'carico_scarico',
    title: 'Allineare registro carico/scarico ammoniaca',
  },
] as const;

// ---------------------------------------------------------------------------
// RNG helpers
// ---------------------------------------------------------------------------

function buildOpenActionTargets(companyCount: number, rng: Rng): number[] {
  const base = OPEN_ACTION_TARGETS.slice(0, Math.min(companyCount, OPEN_ACTION_TARGETS.length));
  const padded = [...base, ...Array(Math.max(0, companyCount - base.length)).fill(0)];
  return shuffle(rng, padded);
}

const MAX_OPEN_NON_CONFORMITIES = OPEN_ACTION_TARGETS.reduce((sum, value) => sum + value, 0);

function capOpenNonConformities(reports: Report[]): void {
  if (MAX_OPEN_NON_CONFORMITIES <= 0) return;

  const entries: Array<{
    report: Report;
    item: Report['data']['nonConformities'][number];
    companyId: string;
    time: number;
  }> = [];

  for (const report of reports) {
    const visitDate = report.meta?.visitDate ?? report.createdAt ?? report.updatedAt ?? '';
    const time = Date.parse(visitDate) || 0;
    for (const item of report.data.nonConformities) {
      if (item.status === 'open') {
        entries.push({
          report,
          item,
          companyId: report.companyId,
          time,
        });
      }
    }
  }

  if (entries.length <= MAX_OPEN_NON_CONFORMITIES) return;

  entries.sort((a, b) => b.time - a.time);
  const openCompanies = new Set<string>();
  let openCount = 0;

  for (const entry of entries) {
    if (openCount >= MAX_OPEN_NON_CONFORMITIES || openCompanies.has(entry.companyId)) {
      entry.item.status = 'closed';
      continue;
    }

    openCompanies.add(entry.companyId);
    openCount += 1;
  }
}

async function capExpiredActions(rng: Rng, maxExpired: number): Promise<void> {
  const actions = await ActionsRepo.listAll();
  const now = new Date();
  const expired = actions.filter((action) => {
    if (action.status === 'completed') return false;
    if (!action.dueDate) return false;
    return new Date(action.dueDate).getTime() < now.getTime();
  });

  const expiredCompleted = actions.filter((action) => {
    if (action.status !== 'completed') return false;
    if (!action.dueDate) return false;
    return new Date(action.dueDate).getTime() < now.getTime();
  });

  const totalExpiredCount = expired.length + expiredCompleted.length;
  if (totalExpiredCount <= maxExpired) return;

  const sortByDueDateDesc = (a: { dueDate?: string }, b: { dueDate?: string }) => {
    const timeA = a.dueDate ? new Date(a.dueDate).getTime() : 0;
    const timeB = b.dueDate ? new Date(b.dueDate).getTime() : 0;
    return timeB - timeA;
  };

  expired.sort(sortByDueDateDesc);
  expiredCompleted.sort(sortByDueDateDesc);

  const keepOpen = expired.slice(0, maxExpired);
  const remainingSlots = Math.max(0, maxExpired - keepOpen.length);
  const keepCompleted = expiredCompleted.slice(0, remainingSlots);
  const keepIds = new Set([...keepOpen, ...keepCompleted].map((action) => action.id));

  const toAdjust = [...expired, ...expiredCompleted].filter((action) => !keepIds.has(action.id));
  const nowIso = new Date().toISOString();

  for (const action of toAdjust) {
    const dueDate = toIsoDate(addDays(now, randomInt(rng, 5, 45)));
    await db.actions.update(action.id, {
      dueDate,
      updatedAt: nowIso,
    });
  }
}

function pickActionType(rng: Rng): 'internal' | 'external' {
  return rng() > 0.25 ? 'external' : 'internal';
}

function buildActionTitle(siteName: string, rng: Rng): string {
  const base = pick(rng, ACTION_TITLE_POOL);
  return `${base} – ${siteName}`;
}

// ---------------------------------------------------------------------------
// Formatting helpers for report values
// ---------------------------------------------------------------------------

function extractNumber(value?: string | number | null): number | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const parsed = Number(
    String(value)
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.')
  );
  return Number.isFinite(parsed) ? parsed : undefined;
}

function normalizeCompliance(
  value?: string
): 'conforme' | 'non_conforme' | 'da_verificare' | 'n_a' {
  if (!value) return 'n_a';
  const normalized = value.toLowerCase();
  if (normalized.includes('non conforme')) return 'non_conforme';
  if (normalized.includes('da verificare')) return 'da_verificare';
  if (normalized.includes('conforme')) return 'conforme';
  return 'n_a';
}

function mapEmergencyMethodToChoice(method: string | undefined, rng: Rng): string {
  const choices = ['bombola_nh3', 'ampolle', 'soluzione', 'altro'];
  if (!method) {
    return pick(rng, choices);
  }
  const normalized = method.toLowerCase();
  if (normalized.includes('bombola') || normalized.includes('ammoniaca')) {
    return 'bombola_nh3';
  }
  if (normalized.includes('ampolla')) return 'ampolle';
  if (normalized.includes('soluzione')) return 'soluzione';
  return 'altro';
}

function mapSimulationPosition(position?: string): string {
  if (!position) return 'altro';
  const normalized = position.toLowerCase();
  if (normalized.includes('terra')) return 'piano_terra';
  if (normalized.includes('primo')) return 'piano_primo';
  if (normalized.includes('copertura')) return 'copertura';
  return 'altro';
}

function seedEmergencyDrillValues({
  report,
  site,
  attendees,
  visitSeed,
  rng,
  visitIso,
}: {
  report: Report;
  site: Site;
  attendees: string[];
  visitSeed: VisitSeedData;
  rng: Rng;
  visitIso: string;
}) {
  const units = site.refrigerationUnits ?? [];
  if (units.length === 0) return;

  const drill = visitSeed.emergencyDrill;
  const responsabili =
    drill?.companyLeads?.join('\n') ??
    attendees.slice(0, 2).join('\n') ??
    'Responsabile stabilimento';
  const verifica = drill?.scope?.toLowerCase().includes('parziale') ? 'parziale' : 'completa';
  const conformita = rng() > 0.2 ? 'conforme' : 'non_conforme';
  const inizio = drill?.startTime ?? '14:00';
  const fine = drill?.endTime ?? '16:00';
  const ultima = drill?.date ?? visitIso;
  const frequenza = 'annuale';
  const metodo = mapEmergencyMethodToChoice(drill?.method ?? drill?.scope, rng);

  units.forEach((_unit, index) => {
    const prefix = `sec_prova-di-emergenza[${index}]`;
    report.data.values[`${prefix}.fld_responsabili`] = responsabili;
    report.data.values[`${prefix}.fld_verifica`] = verifica;
    report.data.values[`${prefix}.fld_conformita-prova`] = conformita;
    report.data.values[`${prefix}.fld_inizio`] = inizio;
    report.data.values[`${prefix}.fld_fine`] = fine;
    report.data.values[`${prefix}.fld_ultima-verifica-completa`] = ultima;
    report.data.values[`${prefix}.fld_frequenza`] = frequenza;
    report.data.values[`${prefix}.fld_metodo-esecuzione`] = metodo;
  });
}

function seedDpiIIISimulationValues({
  report,
  visitSeed,
  rng,
}: {
  report: Report;
  visitSeed: VisitSeedData;
  rng: Rng;
}) {
  const simulation = visitSeed.simulation;
  const participant = simulation?.participants?.[0];
  const tracts = (simulation?.tracts ?? [])
    .map((tract) => tract.description)
    .filter(Boolean)
    .join('\n');
  const difficulties = (simulation?.difficulties ?? []).join('\n');
  const suggestions = (simulation?.suggestions ?? []).join('\n');

  report.data.values['sec_verifica-utilizzo-dpi-iii-categoria-.fld_tempo-preparazione-min'] =
    extractNumber(participant?.prepTime) ?? randomInt(rng, 2, 8);
  report.data.values['sec_verifica-utilizzo-dpi-iii-categoria-.fld_tempo-intervento-min'] =
    extractNumber(participant?.interventionTime) ?? randomInt(rng, 5, 15);
  report.data.values['sec_verifica-utilizzo-dpi-iii-categoria-.fld_pressione-iniziale-bar'] =
    extractNumber(participant?.pressureStart) ?? randomInt(rng, 180, 220);
  report.data.values['sec_verifica-utilizzo-dpi-iii-categoria-.fld_pressione-finale-bar'] =
    extractNumber(participant?.pressureEnd) ?? randomInt(rng, 120, 180);
  report.data.values['sec_verifica-utilizzo-dpi-iii-categoria-.fld_consumo-l-min'] =
    extractNumber(participant?.consumption) ?? randomInt(rng, 20, 40);
  report.data.values['sec_verifica-utilizzo-dpi-iii-categoria-.fld_conforme'] = Boolean(
    participant?.conform?.toLowerCase().includes('conforme')
  );
  report.data.values['sec_verifica-utilizzo-dpi-iii-categoria-.fld_tratti-impianto'] =
    tracts || 'Compressore / Valvola alta pressione';
  report.data.values['sec_verifica-utilizzo-dpi-iii-categoria-.fld_posizione'] =
    mapSimulationPosition(simulation?.position);
  report.data.values['sec_verifica-utilizzo-dpi-iii-categoria-.fld_necessario-affrontare-scale'] =
    simulation?.stairsNeeded ?? rng() > 0.5;
  report.data.values['sec_verifica-utilizzo-dpi-iii-categoria-.fld_modalita-esecuzione'] =
    simulation?.mode ?? 'Partenza da postazione DPI';
  report.data.values['sec_verifica-utilizzo-dpi-iii-categoria-.fld_difficolta-riscontrate'] = [
    difficulties,
    suggestions,
  ]
    .filter(Boolean)
    .join('\n');
}

function registerStateSlug(
  value?: string
): 'aggiornato' | 'da_aggiornare' | 'conforme' | 'non_conforme' {
  if (!value) return 'non_conforme';
  const normalized = value.toLowerCase();
  if (normalized.includes('da aggiornare')) return 'da_aggiornare';
  if (normalized.includes('non conforme')) return 'non_conforme';
  if (normalized.includes('conforme')) return 'conforme';
  if (normalized.includes('aggiornato')) return 'aggiornato';
  return 'non_conforme';
}

function buildInfraSummary(checks: InfrastructureCheckSeed[]): string {
  if (!checks.length) return 'Nessuna verifica registrata';
  const lookup = new Map<InfrastructureFieldId, string>(
    INFRASTRUCTURE_FIELDS.map((field) => [field.id, field.label])
  );
  return checks
    .map((entry) => {
      const fieldId = entry.id as InfrastructureFieldId;
      const label = lookup.get(fieldId) ?? entry.id;
      const status = entry.status ?? 'Non disponibile';
      return `${label}: ${status}`;
    })
    .join('; ');
}

function buildInfraVerificationState(
  checks: InfrastructureCheckSeed[],
  extras?: {
    lightingNotes?: string;
    dvrProcedures?: string;
    safetySuggestions?: string;
  }
): {
  doors: 'conforme' | 'non_conforme' | 'da_verificare' | 'n_a';
  emergencyLightingExit: 'conforme' | 'non_conforme' | 'da_verificare' | 'n_a';
  emergencyLightingIntervention: 'conforme' | 'non_conforme' | 'da_verificare' | 'n_a';
  cctv: 'conforme' | 'non_conforme' | 'da_verificare' | 'n_a';
  intrusion: 'conforme' | 'non_conforme' | 'da_verificare' | 'n_a';
  vvfDocs: 'conforme' | 'non_conforme' | 'da_verificare' | 'n_a';
  lightingNotes?: string;
  dvrProcedures?: string;
  safetySuggestions?: string;
} {
  const lookup = new Map<InfrastructureFieldId, string | undefined>(
    checks.map((entry) => [entry.id as InfrastructureFieldId, entry.status])
  );
  return {
    doors: normalizeCompliance(lookup.get('door-closure')),
    emergencyLightingExit: normalizeCompliance(lookup.get('lighting-exit')),
    emergencyLightingIntervention: normalizeCompliance(lookup.get('lighting-intervention')),
    cctv: normalizeCompliance(lookup.get('cctv')),
    intrusion: normalizeCompliance(lookup.get('intrusion')),
    vvfDocs: normalizeCompliance(lookup.get('vvf-docs')),
    lightingNotes: extras?.lightingNotes,
    dvrProcedures: extras?.dvrProcedures,
    safetySuggestions: extras?.safetySuggestions,
  };
}

function summarizeConductivity(unitChecks: VisitUnitCheck[]): string | undefined {
  if (!unitChecks.length) return undefined;
  const lines = unitChecks
    .map((check) => {
      const value = check.safety.condenserWaterConductivity;
      if (value === undefined || value === null || value === '') {
        return undefined;
      }
      const rendered = typeof value === 'number' ? `${value}` : String(value);
      const notes = check.safety.conductivityNotes;
      return notes
        ? `${check.unitName}: ${rendered} µS — ${notes}`
        : `${check.unitName}: ${rendered} µS`;
    })
    .filter((entry): entry is string => Boolean(entry));

  return lines.length ? lines.join('\n') : undefined;
}

function buildAttendeeEntries(
  company: Company,
  attendeeNames: string[]
): Array<{ name: string; role?: string }> {
  return attendeeNames.map((name) => {
    const person = company.personnel.find((entry) => entry.lastName === name);
    return {
      name,
      role: person?.role,
    };
  });
}

function buildPatentHolderEntries(company: Company): Array<{
  name: string;
  patentExpiry?: string;
  licenseLastUpdate?: string;
  antincendio: boolean;
  primoSoccorso: boolean;
}> {
  return company.personnel
    .filter((person) => person.licensed)
    .map((person) => ({
      name: `${person.firstName} ${person.lastName}`,
      patentExpiry: person.licenseExpiry,
      licenseLastUpdate: person.licenseLastUpdate,
      antincendio: person.fireTraining,
      primoSoccorso: person.firstAidTraining,
    }));
}

function scoreToRating(value: number): number {
  return Math.max(0, Math.min(5, Math.round(value / 2)));
}

function clampVisitScore(value?: number): number {
  if (value === undefined || Number.isNaN(value)) return 0;
  return Math.max(0, Math.min(10, Math.round(value)));
}

const SCORECARD_FIELD_BY_CONTEXT = Object.entries(SCORECARD_FIELD_TO_CONTEXT_KEY).reduce(
  (acc, [fieldId, contextKey]) => {
    acc[contextKey] = fieldId;
    return acc;
  },
  {} as Record<string, string>
);

function buildScorecardEntries(
  rows: ScorecardSeedRow[],
  fallbackDate: string
): Array<{
  date: string;
  disponibilitaPianificazione: number;
  disponibilitaPersonale: number;
  disponibilitaFormazione: number;
  disponibilitaProve: number;
  interesseSopralluogo: number;
  interesseFormazione: number;
  interesseConoscenze: number;
  interesseSicurezza: number;
  efficienzaOrdine: number;
  efficienzaPulizia: number;
  efficienzaRisposta: number;
  efficienzaEsecuzione: number;
  efficienzaPuntualita: number;
  documentaleAccesso: number;
  documentaleCompletezza: number;
  sicurezzaComplessiva: number;
}> {
  return rows.map((row) => ({
    date: row.date ?? fallbackDate,
    disponibilitaPianificazione: scoreToRating(row.planning),
    disponibilitaPersonale: scoreToRating(row.availability),
    disponibilitaFormazione: scoreToRating(row.knowledge),
    disponibilitaProve: scoreToRating(row.efficiency),
    interesseSopralluogo: scoreToRating(row.interest),
    interesseFormazione: scoreToRating(row.knowledge),
    interesseConoscenze: scoreToRating(row.planning),
    interesseSicurezza: scoreToRating(row.overall),
    efficienzaOrdine: scoreToRating(row.order),
    efficienzaPulizia: scoreToRating(row.efficiency),
    efficienzaRisposta: scoreToRating(row.availability),
    efficienzaEsecuzione: scoreToRating(row.efficiency),
    efficienzaPuntualita: scoreToRating(row.planning),
    documentaleAccesso: scoreToRating(row.documentation),
    documentaleCompletezza: scoreToRating(row.documentation),
    sicurezzaComplessiva: scoreToRating(row.overall),
  }));
}

function mapEmergencyMethod(method?: string): 'tabletop' | 'live' | 'partial' | 'full' {
  if (!method) return 'tabletop';
  const normalized = method.toLowerCase();
  if (normalized.includes('scenario') || normalized.includes('aula')) return 'tabletop';
  if (normalized.includes('tempo reale') || normalized.includes('reale')) return 'live';
  if (normalized.includes('parziale')) return 'partial';
  return 'full';
}

function buildEmergencyDrillContext(data?: EmergencyDrillSeed):
  | {
      date?: string;
      method?: 'tabletop' | 'live' | 'partial' | 'full';
      start?: string;
      end?: string;
      responsabili?: string;
      perUnit: Array<{ unitName: string; date?: string }>;
    }
  | undefined {
  if (!data) return undefined;
  const { date, method, startTime, endTime, companyLeads, unitResults } = data;
  return {
    date,
    method: mapEmergencyMethod(method ?? data.scope),
    start: startTime,
    end: endTime,
    responsabili: companyLeads?.join('\n'),
    perUnit: (unitResults ?? []).map((result, index) => ({
      unitName: result.unitLabel ?? `Impianto ${index + 1}`,
      date: result.date,
    })),
  };
}

function normalizeChecklistValue(value?: string): boolean {
  if (!value) return false;
  const normalized = value.toLowerCase();
  return (
    normalized.includes('aggiornato') ||
    normalized.includes('verificato') ||
    normalized.includes('eseguito')
  );
}

function buildTrainingCheckContext(data?: TrainingUpdateSeed):
  | {
      executed?: boolean;
      scheduledFor?: string;
      checklist: {
        pericoli?: boolean;
        procedure?: boolean;
        approfondimenti?: boolean;
        dvrAggiornato?: boolean;
        dpiAggiornati?: boolean;
      };
      participants: Array<{ name: string; signature: boolean }>;
    }
  | undefined {
  if (!data) return undefined;
  const { executed, nextDue, checks, participants } = data;
  const checklist = checks ?? {};
  return {
    executed: executed?.toLowerCase().includes('eseguito'),
    scheduledFor: nextDue,
    checklist: {
      pericoli: normalizeChecklistValue(checklist.hazards),
      procedure: normalizeChecklistValue(checklist.procedures),
      approfondimenti: Boolean(checklist.info),
      dvrAggiornato: normalizeChecklistValue(checklist.dvrUpdate),
      dpiAggiornati: normalizeChecklistValue(checklist.dpiUpdate),
    },
    participants: (participants ?? []).map((participant) => ({
      name: participant.name ?? 'Partecipante',
      signature: Boolean(participant.signature ?? participant.notes),
    })),
  };
}

function buildSimulationContext(data?: SimulationSeed):
  | {
      runs: Array<{
        personName: string;
        prepMin: number;
        workMin: number;
        pStart: number;
        pEnd: number;
        consumo: number;
        conforme: boolean;
        segments: string;
        position?: string;
        scaleNecessarie: 'si' | 'no';
      }>;
    }
  | undefined {
  if (!data) return undefined;
  const { participants, tracts, position, stairsNeeded } = data;
  const segmentList = tracts ?? [];
  const segments = segmentList
    .map((tract) => tract.description)
    .filter(Boolean)
    .join('\n');
  return {
    runs: (participants ?? []).map((participant) => ({
      personName: participant.name ?? 'Operatore',
      prepMin: extractNumber(participant.prepTime) ?? 0,
      workMin: extractNumber(participant.interventionTime) ?? 0,
      pStart: extractNumber(participant.pressureStart) ?? 0,
      pEnd: extractNumber(participant.pressureEnd) ?? 0,
      consumo: extractNumber(participant.consumption) ?? 0,
      conforme: (participant.conform ?? '').toLowerCase().includes('conforme'),
      segments,
      position,
      scaleNecessarie: stairsNeeded ? 'si' : 'no',
    })),
  };
}

function buildInailContext(
  company: Company,
  site: Site
): {
  codiceDitta?: string;
  pat?: string;
  verbaleNumero?: string;
  note?: string;
} {
  const sequence =
    typeof site.sequenceNumber === 'number' && site.sequenceNumber > 0
      ? `S${String(site.sequenceNumber).padStart(2, '0')}`
      : null;
  const pat = company.code && sequence ? `${company.code}-${sequence}` : undefined;
  return {
    codiceDitta: company.code,
    pat,
    verbaleNumero: `IN-${site.id.slice(-4).toUpperCase()}`,
    note: 'Dati generati automaticamente',
  };
}

function createVisitContextData(
  {
    company,
    site,
    visitSeed,
    visitDate,
    inspectorName,
    attendees,
    frequencyMonths,
    rng,
  }: {
    company: Company;
    site: Site;
    visitSeed: VisitSeedData;
    visitDate: Date;
    inspectorName: string;
    attendees: string[];
    frequencyMonths: number;
    rng: Rng;
  },
  overrides: {
    findings?: VisitFinding[];
    actions?: VisitAction[];
  } = {}
): {
  context: TemplateContextOverrides;
  snapshot: VisitContextType;
} {
  const visitIso = toIsoDate(visitDate);
  const attendeeEntries = buildAttendeeEntries(company, attendees);
  const patentHolders = buildPatentHolderEntries(company);
  const registerContexts: Array<{
    type: string;
    periodStart?: string;
    periodEnd: string;
    state: string;
    totals: { entries: number; qtyKg: number };
    attachments: string;
    notes?: string;
  }> = [];
  const visitRegisters: VisitContextType['registers'] = [];

  const registers = visitSeed.registers ?? {};

  REGISTER_DEFINITIONS.forEach((definition) => {
    const registerKey = definition.key as keyof RegistersSeed;
    const record = registers[registerKey];
    const state = registerStateSlug(record?.status);
    const qtyKg =
      definition.key === 'charge' ? (extractNumber(registers.charge?.quantityRecorded) ?? 0) : 0;

    registerContexts.push({
      type: definition.type,
      periodStart: site.periodicityPreviousVisit,
      periodEnd: visitIso,
      state,
      totals: {
        entries: randomInt(rng, 6, 18),
        qtyKg,
      },
      attachments: '',
      notes: record?.notes,
    });

    visitRegisters.push({
      id: `${definition.key}-${site.id}`,
      type: definition.type,
      state: record?.status,
      chargeRecorded: definition.key === 'charge' ? Boolean(record?.status) : undefined,
      chargeKg: definition.key === 'charge' ? qtyKg || undefined : undefined,
      totalKg: definition.key === 'charge' ? qtyKg || undefined : undefined,
      notes: record?.notes,
    });
  });

  const emergencyDrill =
    buildEmergencyDrillContext(visitSeed.emergencyDrill) ??
    ({
      perUnit: [],
    } as {
      perUnit: Array<{ unitName: string; date?: string }>;
    });
  const trainingCheck = buildTrainingCheckContext(visitSeed.trainingUpdate) ?? {
    executed: false,
    scheduledFor: undefined,
    checklist: {
      pericoli: false,
      procedure: false,
      approfondimenti: false,
      dvrAggiornato: false,
      dpiAggiornati: false,
    },
    participants: [],
  };
  const simulation = buildSimulationContext(visitSeed.simulation) ?? {
    runs: [],
  };

  const safetySummary = visitSeed.safetySummary ?? {};

  const additionalChecks = [
    safetySummary.calibrationSummary,
    safetySummary.valvesSummary,
    site.maintenanceNotes,
  ]
    .filter(Boolean)
    .join('\n');

  const metricsExtras = {
    nextCalibrationDate: toIsoDate(addMonths(visitDate, 6)),
    nextValveInspectionDate: toIsoDate(addMonths(visitDate, 12)),
  };

  const infraSnapshot = (() => {
    const checks = visitSeed.infrastructureChecks ?? [];
    if (!checks.length) return undefined;
    const snapshot: VisitContextType['infraVerification'] = {};
    const lightingNotes: string[] = [];
    const safetyNotes: string[] = [];
    for (const entry of checks) {
      const note = entry.notes?.trim();
      switch (entry.id) {
        case 'lighting-exit':
          snapshot.emergencyLightingExit = entry.status;
          if (note) lightingNotes.push(`Esodo: ${note}`);
          break;
        case 'lighting-intervention':
          snapshot.emergencyLightingIntervention = entry.status;
          if (note) lightingNotes.push(`Intervento: ${note}`);
          break;
        case 'door-closure':
          snapshot.doors = entry.status;
          if (note) safetyNotes.push(`Porte: ${note}`);
          break;
        case 'cctv':
          snapshot.cctv = entry.status;
          if (note) safetyNotes.push(`Videosorveglianza: ${note}`);
          break;
        case 'intrusion':
          snapshot.intrusion = entry.status;
          if (note) safetyNotes.push(`Antintrusione: ${note}`);
          break;
        case 'vvf-docs':
          snapshot.safetySuggestions = note ?? entry.status;
          break;
        default:
          break;
      }
    }
    if (lightingNotes.length) {
      snapshot.lightingNotes = lightingNotes.join('\n');
    }
    if (!snapshot.safetySuggestions && safetyNotes.length) {
      snapshot.safetySuggestions = safetyNotes.join('\n');
    }
    return snapshot;
  })();

  const dpiSnapshot = visitSeed.dpiInventory;

  const emergencyDrillSnapshot = (() => {
    const drill = visitSeed.emergencyDrill;
    if (!drill) return undefined;
    return {
      date: drill.date,
      responsabili: drill.companyLeads?.join(', '),
      scope: drill.scope,
      start: drill.startTime,
      end: drill.endTime,
      frequency: 'annuale',
      method: drill.method,
      participants: (drill.companyLeads ?? []).map((name) => ({ name })),
    } satisfies VisitContextType['emergencyDrill'];
  })();

  const trainingSnapshot = visitSeed.trainingUpdate
    ? ({
        executed: trainingCheck.executed,
        scheduledFor: trainingCheck.scheduledFor,
        checklist: trainingCheck.checklist,
        participants: trainingCheck.participants.map((participant) => ({
          name: participant.name,
          signature: participant.signature,
        })),
      } satisfies VisitContextType['trainingVerification'])
    : undefined;

  const simulationSnapshot = (() => {
    const simulationData = visitSeed.simulation;
    if (!simulationData) return undefined;
    const segments = (simulationData.tracts ?? [])
      .map((tract) => tract.description)
      .filter(Boolean)
      .join('\n');
    const runs = (simulationData.participants ?? []).map((participant) => ({
      id: participant.id ?? newId(),
      personName: participant.name ?? 'Operatore',
      prepMin: extractNumber(participant.prepTime),
      workMin: extractNumber(participant.interventionTime),
      pStart: extractNumber(participant.pressureStart),
      pEnd: extractNumber(participant.pressureEnd),
      consumo: extractNumber(participant.consumption),
      conforme: (participant.conform ?? '').toLowerCase().includes('conforme'),
      segments: segments || undefined,
      position: simulationData.position,
      stairsRequired: simulationData.stairsNeeded,
      executionMode: simulationData.mode,
      difficulties: (simulationData.difficulties ?? []).join('\n').trim() || undefined,
      suggestions: (simulationData.suggestions ?? []).join('\n').trim() || undefined,
    }));
    return runs.length ? { runs } : undefined;
  })();

  const scorecardSeedRows = visitSeed.scorecard ?? [];
  const scorecardSnapshot = scorecardSeedRows.length
    ? {
        byDate: scorecardSeedRows.map((row, index) => ({
          id: row.id ?? `score-${site.id}-${index}`,
          date: row.date ?? visitIso,
          disponibilitaPianificazione: clampVisitScore(row.planning),
          disponibilitaPersonale: clampVisitScore(row.availability),
          disponibilitaFormazione: clampVisitScore(row.knowledge),
          disponibilitaProve: clampVisitScore(row.efficiency),
          interesseSopralluogo: clampVisitScore(row.interest),
          interesseFormazione: clampVisitScore(row.knowledge),
          interesseConoscenze: clampVisitScore(row.planning),
          interesseSicurezza: clampVisitScore(row.overall),
          efficienzaOrdine: clampVisitScore(row.order),
          efficienzaPulizia: clampVisitScore(row.efficiency),
          efficienzaRisposta: clampVisitScore(row.availability),
          efficienzaEsecuzione: clampVisitScore(row.efficiency),
          efficienzaPuntualita: clampVisitScore(row.planning),
          documentaleAccesso: clampVisitScore(row.documentation),
          documentaleCompletezza: clampVisitScore(row.documentation),
          sicurezzaComplessiva: clampVisitScore(row.overall),
        })),
      }
    : undefined;

  const visitPeriod =
    site.periodicityControlPeriod ?? formatControlPeriodRange(visitDate, frequencyMonths);

  if (infraSnapshot && !infraSnapshot.dvrProcedures) {
    const dvrFlag = trainingCheck.checklist?.dvrAggiornato;
    infraSnapshot.dvrProcedures =
      dvrFlag === undefined
        ? undefined
        : dvrFlag
          ? 'DVR aggiornato'
          : 'Aggiornare DVR e registrare esito in verbale';
  }

  const visitSnapshot = VisitContext.parse({
    date: visitIso,
    period: visitPeriod,
    frequencyMonths,
    inspector: inspectorName,
    attendees: attendeeEntries,
    infraVerification: infraSnapshot,
    dpiInventory: dpiSnapshot,
    emergencyDrill: emergencyDrillSnapshot,
    trainingVerification: trainingSnapshot,
    dpiIIIcatSimulation: simulationSnapshot,
    unitChecks: visitSeed.unitChecks,
    detailedChecks: visitSeed.detailedChecks,
    registers: visitRegisters,
    inailCompliance: visitSeed.inailCompliance,
    scorecard: scorecardSnapshot,
  });

  const findings = overrides.findings ?? [];
  const actions = overrides.actions ?? [];

  const visit = {
    period: visitPeriod,
    date: visitIso,
    attendeesCount: attendeeEntries.length,
    attendeesPreview: attendeeEntries
      .slice(0, 3)
      .map((entry) => entry.name)
      .join(', '),
    presentPatentedCount: patentHolders.length,
    presentPatentedPreview: patentHolders
      .slice(0, 3)
      .map((entry) => entry.name)
      .join(', '),
    infraVerificationSummary: buildInfraSummary(visitSeed.infrastructureChecks),
    safety: {
      authorizationCompliance: normalizeCompliance(safetySummary.authorizationCompliance),
      activeSensors:
        safetySummary.activeSensors ??
        (() => {
          const numeric = extractNumber(safetySummary.activeSensors);
          return numeric !== undefined ? String(numeric) : undefined;
        })(),
      calibrationSummary: safetySummary.calibrationSummary,
      valvesSummary: safetySummary.valvesSummary,
      conductivitySummary: summarizeConductivity(visitSeed.unitChecks),
    },
    attendees: attendeeEntries,
    presentPatented: patentHolders,
    infraVerification: buildInfraVerificationState(visitSeed.infrastructureChecks, infraSnapshot),
    emergencyDrill,
    unitChecks: visitSeed.unitChecks,
    detailedChecks: visitSeed.detailedChecks,
    dpiInventory: visitSeed.dpiInventory,
    additionalChecks: additionalChecks.length ? additionalChecks : undefined,
    inailCompliance: visitSeed.inailCompliance,
    registers: registerContexts,
    trainingCheck,
    dpiIIIcatSimulation: simulation,
    findings,
    actions,
    scorecard: {
      byDate: buildScorecardEntries(visitSeed.scorecard, site.periodicityPreviousVisit ?? visitIso),
    },
  } as Record<string, unknown>;

  const context = {
    company: {
      ourContact: {
        email: 'tecnico@audit.it',
      },
    },
    visit,
    metrics: metricsExtras,
    attachments: { list: '' },
    inail: buildInailContext(company, site),
    signatures: {
      inspectorName,
      companyRepName: attendeeEntries[0]?.name,
      signedAt: visitIso,
    },
  } as unknown as TemplateContextOverrides;

  return { context, snapshot: visitSnapshot };
}

// ---------------------------------------------------------------------------
// Report enrichment
// ---------------------------------------------------------------------------

function humanFrequencyLabel(frequency: Site['frequency'] | undefined): string {
  switch (frequency) {
    case 'monthly':
      return 'Mensile';
    case 'bimonthly':
      return 'Bimestrale';
    case 'quarterly':
      return 'Trimestrale';
    case 'four_monthly':
      return 'Quadrimestrale';
    case 'semiannual':
      return 'Semestrale';
    case 'annual':
      return 'Annuale';
    default:
      return 'Periodica';
  }
}

function pickAttendees(company: Company, siteId: string, rng: Rng): string[] {
  const relevant = filterPersonnelForSite(company.personnel ?? [], siteId);
  if (!relevant.length) return ['Responsabile stabilimento'];
  const shuffled = shuffle(rng, relevant);
  return shuffled
    .slice(0, randomInt(rng, 1, Math.min(3, relevant.length)))
    .map((p) => `${p.firstName} ${p.lastName}`);
}

function createReportForVisit(
  template: ReportTemplate,
  company: Company,
  siteSeed: GeneratedSite,
  visitSeed: VisitSeedData,
  visitDate: Date,
  inspector: string,
  rng: Rng,
  frequencyMonths: number,
  previousReport?: Report | null
): GeneratedReport {
  const site = siteSeed.site;
  const visitIso = toIsoDate(visitDate);
  const variables = { today: visitIso, inspectorName: inspector };
  const attendees = pickAttendees(company, site.id, rng);
  const changePlan = planDpiAdjustments({
    siteSeed,
    visitSeed,
    previousReport,
    visitDate,
    attendees,
    rng,
  });

  const { context: visitContext, snapshot: visitSnapshot } = createVisitContextData(
    {
      company,
      site,
      visitSeed,
      visitDate,
      inspectorName: inspector,
      attendees,
      frequencyMonths,
      rng,
    },
    {
      findings: changePlan.findings,
      actions: changePlan.actions,
    }
  );

  const baseReport = previousReport
    ? createReportWithTracking({
        template,
        company,
        site,
        variables,
        previousReport,
        context: visitContext,
      })
    : createReportFromTemplate({
        template,
        company,
        site,
        variables,
        context: visitContext,
      });
  const scorecardSeedRows = visitSeed.scorecard ?? [];

  const now = new Date();
  const createdAtCandidate = addDays(visitDate, randomInt(rng, 2, 4));
  const createdAtDate = new Date(
    Math.min(Math.max(createdAtCandidate.getTime(), visitDate.getTime()), now.getTime())
  );

  const updatedAtCandidate = addDays(visitDate, randomInt(rng, 5, 7));
  const updatedAtDate = new Date(
    Math.min(Math.max(updatedAtCandidate.getTime(), createdAtDate.getTime()), now.getTime())
  );

  const finalizedAtCandidate = addDays(visitDate, randomInt(rng, 7, 14));
  const finalizedAtDate = new Date(
    Math.min(Math.max(finalizedAtCandidate.getTime(), updatedAtDate.getTime()), now.getTime())
  );

  const createdAt = toIsoDateTime(createdAtDate);
  const finalizedAt = toIsoDateTime(finalizedAtDate);
  const reportAt = toIsoDateTime(applyWorkingHours(visitDate, rng));
  const plannedVisitAt = baseReport.plannedVisitAt ?? reportAt;

  const report: Report = {
    ...baseReport,
    companyId: company.id,
    siteId: site.id,
    meta: {
      ...baseReport.meta,
      visitDate: visitIso,
      inspector,
      auditType: humanFrequencyLabel(site.frequency),
      gas: site.toxicGas ?? site.gasType ?? '',
      company: company.name,
      siteName: site.name,
      siteLocation: site.sedeOperativa ?? site.address ?? '',
    },
    status: 'finalized',
    createdAt,
    updatedAt: toIsoDateTime(updatedAtDate),
    finalizedAt,
    reportAt,
    plannedVisitAt,
    previousReportId: previousReport?.id,
    data: {
      values: { ...baseReport.data.values },
      comments: baseReport.data.comments ?? {},
      highlights: baseReport.data.highlights,
      nonConformities: [],
    },
  };

  if (scorecardSeedRows.length > 0) {
    const seededEntries = buildScorecardEntries(scorecardSeedRows, visitIso);

    seededEntries.forEach((entry, index) => {
      report.data.values[buildKeypath(SCORECARD_SECTION_ID, SCORECARD_DATE_FIELD_ID, [index])] =
        entry.date;

      Object.entries(SCORECARD_FIELD_BY_CONTEXT).forEach(([contextKey, fieldId]) => {
        const rawValue = entry[contextKey as keyof typeof entry];
        if (typeof rawValue !== 'number') return;
        report.data.values[buildKeypath(SCORECARD_SECTION_ID, fieldId, [index])] =
          clampVisitScore(rawValue);
      });
    });
  }

  seedEmergencyDrillValues({
    report,
    site,
    attendees,
    visitSeed,
    rng,
    visitIso,
  });
  seedDpiIIISimulationValues({ report, visitSeed, rng });

  const followUps: FollowUp[] = changePlan.followUps.map((item) => ({
    id: item.issue.id,
    reportId: report.id,
    siteId: site.id,
    companyId: company.id,
    title: item.title ?? item.issue.description,
    description: item.description,
    severity: 'medium',
    type: item.type ?? 'external',
    fieldPath: item.issue.fieldPath,
    dueDate: item.dueDate,
    visitDate,
    status: item.status,
    frequencyMonths: item.frequencyMonths,
  }));

  const openFollowUpMap = new Map(
    changePlan.followUps
      .filter((item) => item.status === 'open')
      .map((item) => [item.issue.fieldPath, item.dueDate])
  );

  const responsible = attendees[0] ?? 'Responsabile stabilimento';

  const nonConformities: Report['data']['nonConformities'] = changePlan.issuesForReport.map(
    (issue) => ({
      id: issue.id,
      fieldPath: issue.fieldPath,
      severity: 'medium',
      description: issue.description,
      dueDate: toIsoDate(openFollowUpMap.get(issue.fieldPath) ?? addMonths(visitDate, 1)),
      correctiveAction: issue.description,
      owner: responsible,
      status: 'open',
      notes: undefined,
    })
  );

  report.data = {
    ...report.data,
    nonConformities,
  };

  const closureSectionPrefixes = [
    'sec_esiti-non-conformita-rilievi[',
    'sec_azioni-correttive[',
    'sec_scheda-di-valutazione[',
  ];

  for (const key of Object.keys(report.data.values)) {
    const match = key.match(/^(sec_[^\[]+\[)(\d+)(\].*)$/);
    if (!match) continue;
    const [, prefix, indexStr] = match;
    const index = Number(indexStr);
    if (Number.isNaN(index) || index === 0) continue;
    if (closureSectionPrefixes.some((sectionPrefix) => prefix.startsWith(sectionPrefix))) {
      delete report.data.values[key];
    }
  }

  for (const key of Object.keys(report.data.highlights ?? {})) {
    const match = key.match(/^(sec_[^\[]+\[)(\d+)(\].*)$/);
    if (!match) continue;
    const [, prefix, indexStr] = match;
    const index = Number(indexStr);
    if (Number.isNaN(index) || index === 0) continue;
    if (closureSectionPrefixes.some((sectionPrefix) => prefix.startsWith(sectionPrefix))) {
      delete report.data.highlights![key];
    }
  }

  return { report, followUps, visitSnapshot };
}

// ---------------------------------------------------------------------------
// Report history generation
// ---------------------------------------------------------------------------

type VisitPlan = {
  siteSeed: GeneratedSite;
  baseDate: Date;
  sequence: number;
};

type PlannedVisit = VisitPlan & {
  visitDate: Date;
  siteVisitIndex: number;
};

function buildVisitPlan(entry: GeneratedCompany): VisitPlan[] {
  const plans: VisitPlan[] = [];

  for (const siteSeed of entry.sites) {
    for (let index = 0; index < entry.historyLength; index++) {
      plans.push({
        siteSeed,
        sequence: index,
        baseDate: addMonths(siteSeed.startDate, siteSeed.frequencyMonths * index),
      });
    }
  }

  return plans.sort((a, b) => a.baseDate.getTime() - b.baseDate.getTime());
}

function generateCompanyReports(
  template: ReportTemplate,
  entry: GeneratedCompany,
  rng: Rng
): GeneratedReport[] {
  const planner = createWeekdayPlanner(rng);
  const visitPlan = buildVisitPlan(entry);
  const plannedVisits: PlannedVisit[] = [];
  const visitsBySite = new Map<string, PlannedVisit[]>();

  for (const plan of visitPlan) {
    const visitDate = planner.schedule(plan.baseDate);
    const siteId = plan.siteSeed.site.id;
    const planned: PlannedVisit = {
      ...plan,
      visitDate,
      siteVisitIndex: 0,
    };

    const bucket = visitsBySite.get(siteId);
    if (bucket) {
      planned.siteVisitIndex = bucket.length;
      bucket.push(planned);
    } else {
      visitsBySite.set(siteId, [planned]);
    }

    plannedVisits.push(planned);
  }

  const previousReportBySite = new Map<string, Report>();
  const previousVisitBySite = new Map<string, Date>();
  let personnelState = clonePersonnel(entry.company.personnel ?? []);
  const generatedReports: GeneratedReport[] = [];

  for (const visit of plannedVisits) {
    const site = visit.siteSeed.site;
    const siteVisits = visitsBySite.get(site.id) ?? [];
    const previousVisit =
      previousVisitBySite.get(site.id) ??
      addMonths(visit.visitDate, -visit.siteSeed.frequencyMonths);
    const nextVisitEntry = siteVisits[visit.siteVisitIndex + 1];
    const nextVisitDate =
      nextVisitEntry?.visitDate ?? addMonths(visit.visitDate, visit.siteSeed.frequencyMonths);

    site.periodicityPreviousVisit = toIsoDate(previousVisit);
    site.periodicityControlPeriod = formatControlPeriodRange(
      previousVisit,
      visit.siteSeed.frequencyMonths
    );
    site.periodicityNextVisit = toIsoDate(nextVisitDate);
    site.periodicityNextVisitAt = toIsoDateTime(applyWorkingHours(nextVisitDate, rng));

    const evolvedPersonnel = evolvePersonnel(personnelState, visit.visitDate, rng);
    personnelState = clonePersonnel(evolvedPersonnel);

    const companySnapshot: Company & { sites: Site[] } = {
      ...entry.company,
      personnel: clonePersonnel(evolvedPersonnel),
      updatedAt: toIsoDateTime(addDays(visit.visitDate, randomInt(rng, 1, 4))),
      sites: entry.sites.map((seed) => seed.site),
    };

    const inspector = pick(rng, INSPECTORS);
    const previousReport = previousReportBySite.get(site.id);

    const generated = createReportForVisit(
      template,
      companySnapshot,
      visit.siteSeed,
      visit.siteSeed.visitSeed,
      visit.visitDate,
      inspector,
      rng,
      visit.siteSeed.frequencyMonths,
      previousReport ?? undefined
    );

    previousReportBySite.set(site.id, generated.report);
    previousVisitBySite.set(site.id, visit.visitDate);
    generatedReports.push(generated);
  }

  entry.company.personnel = clonePersonnel(personnelState);
  entry.company.updatedAt = toIsoDateTime(new Date());
  entry.input = {
    id: entry.company.id,
    name: entry.company.name,
    code: entry.company.code,
    vat: entry.company.vat,
    sedeLegale: entry.company.sedeLegale,
    notes: entry.company.notes,
    logoUrl: entry.company.logoUrl,
    createdAt: entry.company.createdAt,
    updatedAt: entry.company.updatedAt,
  };

  for (const siteSeed of entry.sites) {
    const siteVisits = visitsBySite.get(siteSeed.site.id) ?? [];
    if (siteVisits.length) {
      const lastVisit = siteVisits[siteVisits.length - 1]!.visitDate;
      const previousVisit =
        siteVisits.length > 1
          ? siteVisits[siteVisits.length - 2]!.visitDate
          : addMonths(lastVisit, -siteSeed.frequencyMonths);
      siteSeed.site.periodicityPreviousVisit = toIsoDate(lastVisit);
      siteSeed.site.periodicityControlPeriod = formatControlPeriodRange(
        previousVisit,
        siteSeed.frequencyMonths
      );
      const futureVisit = planner.schedule(addMonths(lastVisit, siteSeed.frequencyMonths), {
        allowFuture: true,
      });
      siteSeed.site.periodicityNextVisit = toIsoDate(futureVisit);
      siteSeed.site.periodicityNextVisitAt = toIsoDateTime(applyWorkingHours(futureVisit, rng));
      siteSeed.site.updatedAt = toIsoDateTime(addDays(lastVisit, randomInt(rng, 1, 4)));
    }

    siteSeed.input = {
      ...siteSeed.site,
      refrigerationUnits: siteSeed.site.refrigerationUnits,
    };
  }

  return generatedReports;
}

// ---------------------------------------------------------------------------
// Action items & persistence
// ---------------------------------------------------------------------------

async function assignActionItemsAndPersist(
  companyEntry: GeneratedCompany,
  generatedReports: GeneratedReport[],
  persist: boolean,
  companyOpenTarget: number,
  rng: Rng
): Promise<void> {
  const reportsById = new Map<string, Report>();
  const siteFrequencyMonths = new Map<string, number>();
  const followUps = generatedReports.flatMap((item) => item.followUps);
  const maxActionsPerCompany = 3;
  const maxOpenActionsPerCompany = Math.min(companyOpenTarget, 1);

  companyEntry.sites.forEach((seed) => {
    siteFrequencyMonths.set(seed.site.id, seed.frequencyMonths);
  });

  generatedReports.forEach((item) => {
    reportsById.set(item.report.id, item.report);
    const siteId = item.report.siteId;
    const frequency = item.visitSnapshot?.frequencyMonths;
    if (siteId && typeof frequency === 'number') {
      siteFrequencyMonths.set(siteId, frequency);
    }
  });

  if (!followUps.length) {
    if (persist) {
      for (const generated of generatedReports) {
        await ReportsRepo.upsert(generated.report);
      }
    }
    return;
  }

  const sortedFollowUps = [...followUps].sort(
    (a, b) => a.visitDate.getTime() - b.visitDate.getTime()
  );

  const cappedFollowUps = sortedFollowUps.slice(-maxActionsPerCompany);
  const allowedIssueIds = new Set(cappedFollowUps.map((item) => item.id));

  reportsById.forEach((report) => {
    report.data.nonConformities = report.data.nonConformities.filter((item) =>
      allowedIssueIds.has(item.id)
    );
  });

  const openCandidates = cappedFollowUps
    .filter((item) => item.status === 'open')
    .sort((a, b) => b.visitDate.getTime() - a.visitDate.getTime());

  cappedFollowUps.forEach((followUp) => {
    followUp.type = followUp.type ?? 'external';
    followUp.title = followUp.title ?? followUp.description;
  });

  openCandidates.forEach((followUp, index) => {
    const targetStatus = index < maxOpenActionsPerCompany ? 'open' : 'closed';
    followUp.status = targetStatus;
  });

  cappedFollowUps.forEach((followUp) => {
    const frequencyMonths =
      followUp.frequencyMonths ?? siteFrequencyMonths.get(followUp.siteId) ?? 1;
    followUp.dueDate = scheduleActionDueDate({
      rng,
      visitDate: followUp.visitDate,
      frequencyMonths,
      status: followUp.status,
    });
  });

  const latestIssueReport = new Map<string, string>();

  for (const followUp of cappedFollowUps) {
    const report = reportsById.get(followUp.reportId);
    if (!report) continue;
    const nonConformity = report.data.nonConformities.find((item) => item.id === followUp.id);
    if (!nonConformity) continue;

    if (followUp.status === 'open') {
      const previousReportId = latestIssueReport.get(followUp.id);
      if (previousReportId && previousReportId !== followUp.reportId) {
        const previousReport = reportsById.get(previousReportId);
        const previousEntry = previousReport?.data.nonConformities.find(
          (item) => item.id === followUp.id
        );
        if (previousEntry) {
          previousEntry.status = 'closed';
          previousEntry.dueDate = toIsoDate(followUp.dueDate);
        }
      }
      latestIssueReport.set(followUp.id, followUp.reportId);
      nonConformity.status = 'open';
      nonConformity.dueDate = toIsoDate(followUp.dueDate);
    } else {
      nonConformity.status = 'closed';
      nonConformity.dueDate = toIsoDate(followUp.dueDate);
      latestIssueReport.delete(followUp.id);
    }
  }

  if (persist) {
    for (const generated of generatedReports) {
      await ReportsRepo.upsert(generated.report);
    }
  }

  if (!persist) {
    return;
  }

  const actionByIssue = new Map<string, string>();

  for (const followUp of cappedFollowUps) {
    if (followUp.status === 'open') {
      const report = reportsById.get(followUp.reportId);
      const actionId = await ActionsRepo.create({
        companyId: followUp.companyId,
        siteId: followUp.siteId,
        reportId: followUp.reportId,
        reportTitle: report?.meta.title,
        reportVisitDate: report?.meta.visitDate,
        reportSiteName: report?.meta.siteName,
        title: followUp.title,
        type: followUp.type,
        dueDate: toIsoDate(followUp.dueDate),
      });
      actionByIssue.set(followUp.id, actionId);
    } else if (followUp.status === 'closed') {
      const existingId = actionByIssue.get(followUp.id);
      if (existingId) {
        await ActionsRepo.markCompleted(existingId);
      } else {
        const report = reportsById.get(followUp.reportId);
        const actionId = await ActionsRepo.create({
          companyId: followUp.companyId,
          siteId: followUp.siteId,
          reportId: followUp.reportId,
          reportTitle: report?.meta.title,
          reportVisitDate: report?.meta.visitDate,
          reportSiteName: report?.meta.siteName,
          title: followUp.title,
          type: followUp.type,
          dueDate: toIsoDate(followUp.dueDate),
        });
        await ActionsRepo.markCompleted(actionId);
      }
    }
  }

  // Generate system actions for GU publication missing
  if (persist) {
    for (const siteSeed of companyEntry.sites) {
      await syncGuPublicationAction({
        companyId: companyEntry.company.id,
        site: siteSeed.site,
      });
    }
  }
}

function buildContactRows(company: Company): ContactRow[] {
  const now = new Date().toISOString();
  return (company.personnel ?? []).map((person) => {
    const syncMeta = createSyncMetadata();
    const syncUpdatedAt = person.updatedAt ?? now;
    const syncDeletedAt = person.deletedAt;
    return {
      id: person.id,
      companyId: company.id,
      siteId: person.scope === 'site' ? person.siteId : undefined,
      firstName: person.firstName,
      lastName: person.lastName,
      email: person.email,
      phone: person.phone,
      role: person.role,
      scope: person.scope,
      reportRecipient: person.reportRecipient,
      alarmContact: person.alarmContact,
      fireTraining: person.fireTraining,
      firstAidTraining: person.firstAidTraining,
      licensed: person.licensed,
      licenseExpiry: person.licenseExpiry,
      licenseLastUpdate: person.licenseLastUpdate,
      reperibile: person.reperibile,
      createdAt: person.createdAt ?? now,
      updatedAt: person.updatedAt ?? now,
      deletedAt: person.deletedAt,
      syncUpdatedAt,
      syncDeletedAt,
      syncDeviceId: syncMeta.syncDeviceId,
      syncVersion: syncMeta.syncVersion,
    } satisfies ContactRow;
  });
}

function buildUnitRows(companyId: string, site: Site): UnitRow[] {
  const now = new Date().toISOString();
  return (site.refrigerationUnits ?? []).map((unit) => {
    const syncMeta = createSyncMetadata();
    const syncUpdatedAt = unit.updatedAt ?? now;
    const syncDeletedAt = unit.deletedAt;
    return {
      ...unit,
      companyId,
      siteId: site.id,
      syncUpdatedAt,
      syncDeletedAt,
      syncDeviceId: syncMeta.syncDeviceId,
      syncVersion: syncMeta.syncVersion,
    } satisfies UnitRow;
  });
}

async function seedContactsAndUnits(entry: GeneratedCompany): Promise<void> {
  const contactRows = buildContactRows(entry.company);
  if (contactRows.length > 0) {
    await db.contacts.bulkAdd(contactRows);
  }

  const unitRows = entry.sites.flatMap((siteSeed) =>
    buildUnitRows(entry.company.id, siteSeed.site)
  );
  if (unitRows.length > 0) {
    await db.units.bulkAdd(unitRows);
    await Promise.all(
      entry.sites.map((siteSeed) => {
        const activeCount =
          siteSeed.site.refrigerationUnits?.filter((unit) => !unit.deletedAt).length ?? 0;
        return db.sites.update(siteSeed.site.id, {
          refrigerationUnitsCount: activeCount,
        });
      })
    );
  }
}

// ---------------------------------------------------------------------------
// Scenario generation
// ---------------------------------------------------------------------------

async function generateScenario(
  template: ReportTemplate,
  options: ScenarioOptions
): Promise<ScenarioResult> {
  const companyCount = options.companyCount ?? TOTAL_COMPANIES;
  const historyBase = options.reportHistoryLength ?? REPORT_HISTORY_BASE;
  const persist = options.persist ?? false;
  const rng = createRng(options.seed ?? DEFAULT_SEED);

  const generatedCompanies = Array.from({ length: companyCount }, (_unused, index) =>
    generateCompany(index, rng, historyBase + index)
  );

  const openTargets = buildOpenActionTargets(companyCount, rng);

  const companies: Company[] = [];
  const sites: Site[] = [];
  const reports: Report[] = [];

  for (const [index, entry] of generatedCompanies.entries()) {
    const companyReports = generateCompanyReports(template, entry, rng);

    if (persist) {
      await CompaniesRepo.saveCompany(entry.input);
      for (const siteSeed of entry.sites) {
        await SitesRepo.saveSite(siteSeed.input);
      }
      await seedContactsAndUnits(entry);
    }

    const companyOpenTarget = openTargets[index] ?? 0;
    await assignActionItemsAndPersist(entry, companyReports, persist, companyOpenTarget, rng);

    companies.push(entry.company);
    sites.push(...entry.sites.map((site) => site.site));
    reports.push(...companyReports.map((item) => item.report));
  }

  capOpenNonConformities(reports);

  // Convert some recent reports to drafts for the Home screen sections
  // - Some with visitDate (show in "Bozze Report")
  // - Some without visitDate (show in "Sopralluoghi da pianificare")
  const draftCandidates = reports
    .filter((r) => r.status === 'finalized')
    .sort((a, b) => {
      const aDate = a.meta?.visitDate ?? a.createdAt ?? '';
      const bDate = b.meta?.visitDate ?? b.createdAt ?? '';
      return bDate.localeCompare(aDate); // Most recent first
    });

  // Create 3-5 draft reports with visit dates (Bozze Report)
  const draftsWithDate = draftCandidates.slice(0, Math.min(4, draftCandidates.length));
  for (const report of draftsWithDate) {
    report.status = 'draft';
    report.finalizedAt = undefined;
  }

  // Create 2-3 draft reports without visit dates (Sopralluoghi da pianificare)
  // Use empty string for visitDate as the schema requires a string
  const draftsWithoutDate = draftCandidates.slice(4, Math.min(7, draftCandidates.length));
  for (const report of draftsWithoutDate) {
    report.status = 'draft';
    report.finalizedAt = undefined;
    report.meta = {
      ...report.meta,
      visitDate: '',
    };
    report.reportAt = undefined;
    report.plannedVisitAt = undefined;
  }

  if (persist) {
    for (const report of reports) {
      await ReportsRepo.upsert(report);
    }
    await capExpiredActions(rng, 3);

    // Sync unified actions from reports, companies, and sites
    // This creates NC, training, calibration, certification, and schedule-inspection actions
    const companiesById = new Map(companies.map((c) => [c.id, c]));
    const sitesById = new Map(sites.map((s) => [s.id, s]));

    for (const report of reports) {
      const company = companiesById.get(report.companyId);
      const site = sitesById.get(report.siteId);
      await ActionSyncService.syncReportActions(report, company, site);
    }

    for (const company of companies) {
      await ActionSyncService.syncCertificationActions(company);
    }

    for (const site of sites) {
      const company = companiesById.get(site.companyId);
      if (company) {
        await ActionSyncService.syncScheduleInspectionAction(site, company);
      }
    }
  }

  return { companies, sites, reports };
}

// ---------------------------------------------------------------------------
// Database helpers
// ---------------------------------------------------------------------------

async function clearDatabase(): Promise<void> {
  await db.transaction(
    'rw',
    [
      db.companies,
      db.sites,
      db.contacts,
      db.units,
      db.reports,
      db.actions,
      db.schemas,
      db.templates,
      db.kv,
      db.meta,
      db.media,
      db.componentCategories,
      db.syncOutbox,
    ],
    async () => {
      await Promise.all([
        db.companies.clear(),
        db.sites.clear(),
        db.contacts.clear(),
        db.units.clear(),
        db.reports.clear(),
        db.actions.clear(),
        db.schemas.clear(),
        db.templates.clear(),
        db.kv.clear(),
        db.meta.clear(),
        db.media.clear(),
        db.componentCategories.clear(),
        db.syncOutbox.clear(),
      ]);
    }
  );
}

function templateStructureSignature(template: ReportTemplate): string {
  const pickFields = (fields: ReportTemplate['sections'][number]['fields']) =>
    (fields ?? []).map((field) => {
      const { id, label, type, source, options, validation, width } = field;
      return {
        id,
        label,
        type,
        source,
        options,
        validation,
        width,
      };
    });

  const pickRows = (rows: ReportTemplate['sections'][number]['rows']) =>
    (rows ?? []).map((row) => ({
      id: row.id,
      label: row.label,
      fieldIds: row.fieldIds,
    }));

  const pickViewBlocks = (viewBlocks: ReportTemplate['sections'][number]['viewBlocks']) =>
    (viewBlocks ?? []).map((block) => {
      if (block.type === 'table') {
        return { id: block.id, type: block.type };
      }
      return { type: block.type };
    });

  const normalized = {
    id: template.id,
    version: template.version,
    metadata: {
      locale: template.metadata.locale,
    },
    groups: (template.groups ?? []).map((group) => ({
      id: group.id,
      label: group.label,
      order: group.order,
      icon: group.icon,
    })),
    sections: template.sections.map((section) => ({
      id: section.id,
      name: section.name,
      type: section.type,
      repeat: section.repeat,
      navigationGroupId: section.navigationGroup?.id,
      layout: section.layout,
      disableHighlight: section.disableHighlight,
      fields: pickFields(section.fields),
      rows: pickRows(section.rows),
      viewBlocks: pickViewBlocks(section.viewBlocks),
    })),
  };

  return JSON.stringify(normalized);
}

async function ensureTemplate(templateId: string): Promise<ReportTemplate> {
  const defaultTemplate = createDefaultTemplate();
  await TemplatesRepo.initialize(defaultTemplate);

  const template = await TemplatesRepo.getLatest(templateId);
  const defaultSignature = templateStructureSignature(defaultTemplate);
  const storedSignature = template && templateStructureSignature(template);

  if (!template || storedSignature !== defaultSignature) {
    console.warn('[SeedData] Template mismatch detected; refreshing template before seeding');
    await TemplatesRepo.upsert(defaultTemplate);
    return defaultTemplate;
  }
  return template;
}

export async function buildSeedScenario(
  template: ReportTemplate,
  options: ScenarioOptions = {}
): Promise<ScenarioResult> {
  return generateScenario(template, options);
}

// ---------------------------------------------------------------------------
// Activity Seeding
// ---------------------------------------------------------------------------

interface ActivitySeedConfig {
  title: string;
  description: string;
  category: ActivityCategory;
  daysFromNow: number; // negative = past (overdue), positive = future
  priority: 'low' | 'medium' | 'high';
}

const ACTIVITY_TEMPLATES: ActivitySeedConfig[] = [
  {
    title: 'Pianifica sopralluogo',
    description: 'Stabilimento: {siteName}\nScadenza prevista: {dueMonth}',
    category: 'inspection-reminder',
    daysFromNow: -5, // overdue
    priority: 'high',
  },
  {
    title: 'Pianifica sopralluogo',
    description: 'Stabilimento: {siteName}\nScadenza prevista: {dueMonth}',
    category: 'inspection-reminder',
    daysFromNow: 14,
    priority: 'medium',
  },
  {
    title: 'Completa report',
    description: 'Report: {reportTitle}\nStabilimento: {siteName}',
    category: 'manual',
    daysFromNow: 7,
    priority: 'medium',
  },
  {
    title: 'Rinnovo CPI',
    description: 'Certificato prevenzione incendi in scadenza',
    category: 'cpi-renewal',
    daysFromNow: 45,
    priority: 'high',
  },
  {
    title: 'Rinnovo autorizzazione',
    description: 'Autorizzazione gas tossico in scadenza',
    category: 'authorization',
    daysFromNow: 30,
    priority: 'high',
  },
  {
    title: 'Rinnovo patentino',
    description: 'Patentino in scadenza per {personName}',
    category: 'certification',
    daysFromNow: -2, // overdue
    priority: 'high',
  },
  {
    title: 'Sopralluogo in programma',
    description: 'Sopralluogo pianificato',
    category: 'inspection-reminder',
    daysFromNow: 21,
    priority: 'high',
  },
  {
    title: 'Completa report',
    description: 'Report bozza da finalizzare',
    category: 'manual',
    daysFromNow: 10,
    priority: 'medium',
  },
];

async function seedActivities(scenario: ScenarioResult, rng: Rng): Promise<void> {
  const now = new Date();
  const activities: ActivityRow[] = [];
  const createSyncMetadata = () => ({
    syncVersion: 0 as const,
    syncUpdatedAt: now.toISOString(),
    syncDeletedAt: undefined,
  });

  // Pick a sample of sites and reports to create activities for
  const sampleSites = shuffle(rng, scenario.sites).slice(0, Math.min(6, scenario.sites.length));
  const draftReports = scenario.reports.filter((r) => r.status === 'draft');
  const sampleDrafts = shuffle(rng, draftReports).slice(0, Math.min(3, draftReports.length));

  let activityIndex = 0;

  // Create scheduling/inspection activities for sites
  for (const site of sampleSites) {
    const templateIndex = activityIndex % ACTIVITY_TEMPLATES.length;
    const template = ACTIVITY_TEMPLATES[templateIndex]!;

    // Skip draft-report templates for site activities
    if (template.category === 'manual' && template.title.includes('report')) {
      activityIndex++;
      continue;
    }

    const dueDate = new Date(now);
    dueDate.setDate(dueDate.getDate() + template.daysFromNow);

    const dueMonth = dueDate.toLocaleDateString('it-IT', {
      month: 'long',
      year: 'numeric',
    });

    activities.push({
      id: newId(),
      companyId: site.companyId,
      siteId: site.id,
      title: `${template.title}: ${site.name}`,
      description: template.description
        .replace('{siteName}', site.name)
        .replace('{dueMonth}', dueMonth),
      type: 'system',
      category: template.category,
      status: 'pending',
      priority: template.priority,
      dueDate: toIsoDate(dueDate),
      sourceType: 'schedule',
      sourceId: `seed-${site.id}-${activityIndex}`,
      triggerDate: toIsoDate(now),
      createdAt: now.toISOString(),
      updatedAt: now.toISOString(),
      ...createSyncMetadata(),
    });

    activityIndex++;
  }

  // Create draft report completion activities
  for (const report of sampleDrafts) {
    const daysOffset = randomInt(rng, 5, 20);
    const dueDate = new Date(now);
    dueDate.setDate(dueDate.getDate() + daysOffset);

    const site = scenario.sites.find((s) => s.id === report.siteId);
    const siteName = site?.name ?? 'Stabilimento';
    const reportTitle = report.meta?.title ?? report.id.slice(0, 8);

    activities.push({
      id: newId(),
      companyId: report.companyId,
      siteId: report.siteId,
      reportId: report.id,
      reportTitle,
      reportSiteName: siteName,
      title: `Completa report: ${reportTitle}`,
      description: `Report: ${reportTitle}\nStabilimento: ${siteName}`,
      type: 'system',
      category: 'manual',
      status: 'pending',
      priority: 'medium',
      dueDate: toIsoDate(dueDate),
      sourceType: 'report',
      sourceId: `complete-report:${report.id}`,
      triggerDate: toIsoDate(now),
      createdAt: now.toISOString(),
      updatedAt: now.toISOString(),
      ...createSyncMetadata(),
    });
  }

  // Add a couple of in-progress activities
  if (activities.length > 2) {
    activities[1]!.status = 'in_progress';
    activities[1]!.startedAt = now.toISOString();
  }

  // Persist activities
  if (activities.length > 0) {
    await db.activities.bulkAdd(activities);
    console.log(`[SeedData]Creati ${activities.length} attività di esempio`);
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

async function seedDatabase(
  options: {
    companyCount?: number;
    templateId?: string;
    seed?: number;
  } = {}
): Promise<void> {
  const {
    companyCount = TOTAL_COMPANIES,
    templateId = 'tpl-ammonia-audit',
    seed = DEFAULT_SEED,
  } = options;

  console.log('[SeedData]Avvio seed database...');

  // Initialize catalogs before seeding data
  console.log('[SeedData]Initializing catalogs...');
  const { catalogService } = await import('../../services/catalogService');
  await catalogService.initializeIfEmpty();
  console.log('[SeedData]Catalogs initialized');

  const template = await ensureTemplate(templateId);

  const scenario = await generateScenario(template, {
    companyCount,
    reportHistoryLength: REPORT_HISTORY_BASE,
    persist: true,
    seed,
  });

  if (scenario.reports.length) {
    const lastReport = scenario.reports[scenario.reports.length - 1];
    console.log('[SeedData]Ultimo report generato', {
      id: lastReport.id,
      companyId: lastReport.companyId,
      siteId: lastReport.siteId,
      visitDate: lastReport.meta.visitDate,
      status: lastReport.status,
    });
  }

  console.log(
    `[SeedData]Creati ${scenario.companies.length} clienti, ${scenario.sites.length} impianti, ${scenario.reports.length} report`
  );

  // Seed realistic activities
  const rng = createRng(seed);
  await seedActivities(scenario, rng);
}

async function seedIfEmpty(options?: Parameters<typeof seedDatabase>[0]): Promise<void> {
  const companies = await CompaniesRepo.listCompanies();
  if (companies.length > 0) {
    console.log(`[SeedData]Database già popolato con ${companies.length} aziende, seed ignorato`);
    return;
  }
  await seedDatabase(options);
  await writeCurrentDevDataVersion();
}

async function seedIfEmptyOnce(options?: Parameters<typeof seedDatabase>[0]): Promise<void> {
  if (typeof localStorage !== 'undefined' && localStorage.getItem(SEED_STORAGE_KEY)) {
    return;
  }

  const companies = await CompaniesRepo.listCompanies();
  if (companies.length > 0) {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(SEED_STORAGE_KEY, 'true');
    }
    return;
  }

  try {
    await seedDatabase(options);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(SEED_STORAGE_KEY, 'true');
    }
    await writeCurrentDevDataVersion();
  } catch (error) {
    console.error('[SeedData]Seed fallito:', error);
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(SEED_STORAGE_KEY);
    }
    throw error;
  }
}

function resetSeedingFlag(): void {
  if (typeof localStorage === 'undefined') {
    return;
  }

  localStorage.removeItem(SEED_STORAGE_KEY);
  for (const legacyKey of LEGACY_SEED_STORAGE_KEYS) {
    localStorage.removeItem(legacyKey);
  }
}

async function forceSeed(options?: Parameters<typeof seedDatabase>[0]): Promise<void> {
  resetSeedingFlag();
  await clearDatabase();
  await seedDatabase(options);
  await writeCurrentDevDataVersion();
}

export const SeedData = {
  clearAndSeed: async (options?: Parameters<typeof seedDatabase>[0]) => {
    await clearDatabase();
    await seedDatabase(options);
    await writeCurrentDevDataVersion();
  },
  seedIfEmpty,
  seedIfEmptyOnce,
  forceSeed,
  resetSeedingFlag,
  exportBundle: () => captureDatabaseBundle(),
  importBundle: async (bundle: DataBundle) => {
    await replaceDatabaseFromBundle(bundle);
  },
};
type VisitFinding = {
  code: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  unitName?: string;
  text: string;
  regulationRefs?: string;
};

type VisitAction = {
  title: string;
  ownerName: string;
  due: string;
  status: 'open' | 'in_progress' | 'done' | 'waived';
  findingCode: string;
};

type PlannedFollowUp = {
  issue: SiteIssueRecord;
  status: 'open' | 'closed';
  description: string;
  dueDate: Date;
  type: 'system' | 'internal' | 'external';
  title: string;
  frequencyMonths: number;
};

type DpiComplianceDescriptor = {
  path: string;
  label: string;
  type: 'choice' | 'bool';
  conformValue: string | boolean;
  nonConformingValues: Array<string | boolean>;
  get: (inventory: VisitDpiInventory) => string | boolean | undefined;
  set: (inventory: VisitDpiInventory, value: string | boolean | undefined) => void;
};

const DPI_SECTION_LABEL = 'Verifica DPI';
const DPI_SECTION_ID = sectionId(DPI_SECTION_LABEL);

function normalizeDpiInventory(source?: VisitDpiInventory): VisitDpiInventory {
  return {
    general: { ...(source?.general ?? {}) },
    maskK: { ...(source?.maskK ?? {}) },
    filtersK: { ...(source?.filtersK ?? {}) },
    scba: { ...(source?.scba ?? {}) },
    scbaMask: { ...(source?.scbaMask ?? {}) },
    suits: { ...(source?.suits ?? {}) },
    gloves: { ...(source?.gloves ?? {}) },
    boots: { ...(source?.boots ?? {}) },
    signage: { ...(source?.signage ?? {}) },
    firstAidKit: source?.firstAidKit,
    eyeWash: source?.eyeWash,
    notes: source?.notes,
  };
}

const DPI_COMPLIANCE_FIELDS: readonly DpiComplianceDescriptor[] = (() => {
  const usedIds = new Set<string>();
  const descriptors: DpiComplianceDescriptor[] = [];

  const nextId = (label: string) => {
    const id = generateHumanReadableId({
      prefix: 'fld',
      label,
      existingIds: usedIds,
    });
    usedIds.add(id);
    return id;
  };

  const addChoice = (
    label: string,
    conformValue: string,
    nonConformingValues: string[],
    getter: (inventory: VisitDpiInventory) => string | undefined,
    setter: (inventory: VisitDpiInventory, value: string | undefined) => void
  ) => {
    descriptors.push({
      path: buildKeypath(DPI_SECTION_ID, nextId(label)),
      label,
      type: 'choice',
      conformValue,
      nonConformingValues,
      get: getter,
      set: (inventory, value) =>
        setter(inventory, typeof value === 'string' || value === undefined ? value : String(value)),
    });
  };

  const addBool = (
    label: string,
    getter: (inventory: VisitDpiInventory) => boolean | undefined,
    setter: (inventory: VisitDpiInventory, value: boolean | undefined) => void
  ) => {
    descriptors.push({
      path: buildKeypath(DPI_SECTION_ID, nextId(label)),
      label,
      type: 'bool',
      conformValue: true,
      nonConformingValues: [false],
      get: getter,
      set: (inventory, value) =>
        setter(inventory, value === undefined ? undefined : Boolean(value)),
    });
  };

  // Dotazione generale
  addChoice(
    'Completezza',
    'conforme',
    ['non_conforme'],
    (inventory) => inventory.general?.completeness,
    (inventory, value) => {
      inventory.general = inventory.general ?? {};
      inventory.general.completeness = value;
    }
  );
  addChoice(
    'Accessibilità',
    'conforme',
    ['non_conforme'],
    (inventory) => inventory.general?.accessibility,
    (inventory, value) => {
      inventory.general = inventory.general ?? {};
      inventory.general.accessibility = value;
    }
  );
  addChoice(
    'Stato conservazione',
    'conforme',
    ['non_conforme'],
    (inventory) => inventory.general?.condition,
    (inventory, value) => {
      inventory.general = inventory.general ?? {};
      inventory.general.condition = value;
    }
  );
  addBool(
    'Chiavi armadio presenti',
    (inventory) => inventory.general?.keysPresent,
    (inventory, value) => {
      inventory.general = inventory.general ?? {};
      inventory.general.keysPresent = value ?? undefined;
    }
  );

  // Maschera filtro K
  addChoice(
    'Stato conservazione',
    'conforme',
    ['non_conforme'],
    (inventory) => inventory.maskK?.condition,
    (inventory, value) => {
      inventory.maskK = inventory.maskK ?? {};
      inventory.maskK.condition = value;
    }
  );
  addChoice(
    'Predisposizione uso',
    'corretta',
    ['non_corretta'],
    (inventory) => inventory.maskK?.readiness,
    (inventory, value) => {
      inventory.maskK = inventory.maskK ?? {};
      inventory.maskK.readiness = value;
    }
  );

  // Autorespiratore
  addChoice(
    'Conformità',
    'conforme',
    ['non_conforme'],
    (inventory) => inventory.scba?.compliance,
    (inventory, value) => {
      inventory.scba = inventory.scba ?? {};
      inventory.scba.compliance = value;
    }
  );

  // Maschera autorespiratore
  addChoice(
    'Stato conservazione',
    'conforme',
    ['non_conforme'],
    (inventory) => inventory.scbaMask?.condition,
    (inventory, value) => {
      inventory.scbaMask = inventory.scbaMask ?? {};
      inventory.scbaMask.condition = value;
    }
  );
  addChoice(
    'Predisposizione uso',
    'corretta',
    ['non_corretta'],
    (inventory) => inventory.scbaMask?.readiness,
    (inventory, value) => {
      inventory.scbaMask = inventory.scbaMask ?? {};
      inventory.scbaMask.readiness = value;
    }
  );

  // Tute, guanti, stivali
  addChoice(
    'Conformità',
    'conforme',
    ['non_conforme'],
    (inventory) => inventory.suits?.compliance,
    (inventory, value) => {
      inventory.suits = inventory.suits ?? {};
      inventory.suits.compliance = value;
    }
  );
  addChoice(
    'Guanti - conformità',
    'conforme',
    ['non_conforme'],
    (inventory) => inventory.gloves?.compliance,
    (inventory, value) => {
      inventory.gloves = inventory.gloves ?? {};
      inventory.gloves.compliance = value;
    }
  );
  addChoice(
    'Stivali - conformità',
    'conforme',
    ['non_conforme'],
    (inventory) => inventory.boots?.compliance,
    (inventory, value) => {
      inventory.boots = inventory.boots ?? {};
      inventory.boots.compliance = value;
    }
  );

  // Altri dispositivi
  addChoice(
    'Cassetta pronto soccorso',
    'conforme',
    ['non_presente', 'non_conforme'],
    (inventory) => inventory.firstAidKit,
    (inventory, value) => {
      inventory.firstAidKit = value;
    }
  );
  addChoice(
    'Cartellonistica',
    'conforme',
    ['integrare', 'non_conforme'],
    (inventory) => inventory.signage?.state,
    (inventory, value) => {
      inventory.signage = inventory.signage ?? {};
      inventory.signage.state = value;
    }
  );
  addChoice(
    'Doccia lavaocchi',
    'conforme',
    ['non_conforme'],
    (inventory) => inventory.eyeWash,
    (inventory, value) => {
      inventory.eyeWash = value;
    }
  );

  return descriptors;
})();

const DPI_DESCRIPTOR_BY_PATH = new Map<string, DpiComplianceDescriptor>(
  DPI_COMPLIANCE_FIELDS.map((descriptor) => [descriptor.path, descriptor])
);

function isDescriptorNonConforming(
  descriptor: DpiComplianceDescriptor,
  value: string | boolean | undefined
): boolean {
  if (value === undefined) return false;
  if (descriptor.type === 'bool') {
    return value === false;
  }
  return descriptor.nonConformingValues.includes(value);
}

function buildIssueDescription(label: string): string {
  return `Ripristinare ${label.toLowerCase()}`;
}

function planDpiAdjustments({
  siteSeed,
  visitSeed,
  previousReport,
  visitDate,
  attendees,
  rng,
}: {
  siteSeed: GeneratedSite;
  visitSeed: VisitSeedData;
  previousReport?: Report | null;
  visitDate: Date;
  attendees: string[];
  rng: Rng;
}): {
  findings: VisitFinding[];
  actions: VisitAction[];
  followUps: PlannedFollowUp[];
  activeIssues: SiteIssueRecord[];
  issuesForReport: SiteIssueRecord[];
} {
  visitSeed.dpiInventory = normalizeDpiInventory(visitSeed.dpiInventory);
  const inventory = visitSeed.dpiInventory;
  const issues = siteSeed.issues;
  const followUps: PlannedFollowUp[] = [];
  const ownerName = attendees[0] ?? 'Responsabile stabilimento';

  const openIssuePaths = new Set(issues.keys());
  const newIssues: SiteIssueRecord[] = [];
  const resolvedIssues: SiteIssueRecord[] = [];
  const maxActiveIssues = 1;
  let pendingNewIssues = 0;

  if (previousReport) {
    const desiredChanges = Math.min(
      3,
      Math.max(1, openIssuePaths.size > 0 ? 2 : 1 + (rng() > 0.6 ? 1 : 0))
    );

    const chosen = new Set<string>();

    if (openIssuePaths.size > 0) {
      const paths = Array.from(openIssuePaths);
      const toResolve = pick(rng, paths);
      const descriptor = DPI_DESCRIPTOR_BY_PATH.get(toResolve);
      if (descriptor) {
        descriptor.set(inventory, descriptor.conformValue as string | boolean);
        chosen.add(descriptor.path);
      }
    }

    let remaining = desiredChanges - chosen.size;
    const availableDescriptors = DPI_COMPLIANCE_FIELDS.filter(
      (descriptor) => !chosen.has(descriptor.path)
    );

    while (remaining > 0 && availableDescriptors.length > 0) {
      const candidates = availableDescriptors.filter((descriptor) => {
        const current = descriptor.get(inventory);
        return !isDescriptorNonConforming(descriptor, current);
      });
      const pool = candidates.length ? candidates : availableDescriptors;
      const descriptor = pick(rng, pool);

      const shouldIntroduceIssue = issues.size + pendingNewIssues < maxActiveIssues && rng() < 0.5;

      descriptor.set(
        inventory,
        shouldIntroduceIssue
          ? (descriptor.nonConformingValues[
              randomInt(rng, 0, descriptor.nonConformingValues.length - 1)
            ] as string | boolean)
          : (descriptor.conformValue as string | boolean)
      );

      if (!shouldIntroduceIssue) {
        // Maintain conformity; don't count towards chosen set to avoid
        // exhausting available descriptors unfairly.
        remaining -= 1;
        continue;
      }

      chosen.add(descriptor.path);
      pendingNewIssues += 1;
      remaining -= 1;
    }
  }

  for (const descriptor of DPI_COMPLIANCE_FIELDS) {
    const value = descriptor.get(inventory);
    const path = descriptor.path;
    const nonConforming = isDescriptorNonConforming(descriptor, value);
    const existing = issues.get(path);

    if (nonConforming) {
      if (!existing) {
        const issue: SiteIssueRecord = {
          id: newId(),
          code: `F-${String(siteSeed.nextIssueIndex).padStart(3, '0')}`,
          fieldPath: path,
          label: descriptor.label,
          description: buildIssueDescription(descriptor.label),
          openedAt: visitDate,
        };
        siteSeed.nextIssueIndex += 1;
        issues.set(path, issue);
        newIssues.push(issue);
      }
    } else if (existing) {
      resolvedIssues.push(existing);
      issues.delete(path);
    }
  }

  const cadenceMonths = Math.max(1, siteSeed.frequencyMonths);
  const dueDate = addMonths(visitDate, cadenceMonths);

  for (const issue of newIssues) {
    const type = pickActionType(rng);
    followUps.push({
      issue,
      status: 'open',
      description: issue.description,
      dueDate,
      type,
      title: buildActionTitle(siteSeed.site.name, rng),
      frequencyMonths: cadenceMonths,
    });
  }

  for (const issue of resolvedIssues) {
    const type = pickActionType(rng);
    followUps.push({
      issue,
      status: 'closed',
      description: `Chiusura ${issue.description.toLowerCase()}`,
      dueDate: visitDate,
      type,
      title: buildActionTitle(siteSeed.site.name, rng),
      frequencyMonths: cadenceMonths,
    });
  }

  const activeIssues = Array.from(issues.values());

  const reportFindings: VisitFinding[] = newIssues.map((issue) => ({
    code: issue.code,
    severity: 'medium',
    text: issue.description,
  }));

  const reportActions: VisitAction[] = newIssues.map((issue) => ({
    title: issue.description,
    ownerName,
    due: toIsoDate(dueDate),
    status: 'open',
    findingCode: issue.code,
  }));

  return {
    findings: reportFindings,
    actions: reportActions,
    followUps,
    activeIssues,
    issuesForReport: newIssues,
  };
}
